import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthenticatedHeader from '../../components/ui/AuthenticatedHeader';
import NavigationBreadcrumbs from '../../components/ui/NavigationBreadcrumbs';
import Button from '../../components/ui/Button';

import CustomerInfoSection from './components/CustomerInfoSection';
import PropertyInfoSection from './components/PropertyInfoSection';
import ReferralInfoSection from './components/ReferralInfoSection';
import FormProgressIndicator from './components/FormProgressIndicator';
import SuccessModal from './components/SuccessModal';
import { leadService } from '../../services/leadService';

const CreateLead = () => {
  const navigate = useNavigate();
  
  // Helper function to migrate old enum values to new format
  const migrateEnumValues = (draftData) => {
    // Migration map for referral_type
    const referralTypeMap = {
      'cesspool-conversion': 'Cesspool Conversion',
      'septic-maintenance': 'Septic Maintenance',
      'other': 'Other'
    };
    
    // Migration map for property_type
    const propertyTypeMap = {
      'residential': 'Residential',
      'commercial': 'Commercial',
      'industrial': 'Industrial'
    };
    
    // Migrate referralType if it exists and is in old format
    if (draftData?.referralType && referralTypeMap?.[draftData?.referralType]) {
      draftData.referralType = referralTypeMap?.[draftData?.referralType];
    }
    
    // Migrate propertyType if it exists and is in old format
    if (draftData?.propertyType && propertyTypeMap?.[draftData?.propertyType]) {
      draftData.propertyType = propertyTypeMap?.[draftData?.propertyType];
    }
    
    return draftData;
  };
  
  const [formData, setFormData] = useState({
    customerFirstName: '',
    customerLastName: '',
    customerEmail: '',
    customerPhone: '',
    customerAddress: '',
    customerCity: '',
    customerState: '',
    customerZip: '',
    propertyType: '',
    homeOwner: '',
    estimatedValue: '',
    propertyAddress: '',
    propertyCity: '',
    propertyState: 'Hawaii',
    propertyZip: '',
    referralType: '',
    notes: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [salesforceLeadId, setSalesforceLeadId] = useState('');
  const [completionPercentage, setCompletionPercentage] = useState(0);
  const [completedSections, setCompletedSections] = useState({
    customer: false,
    property: false,
    referral: false
  });
  const [currentDraftId, setCurrentDraftId] = useState(null);
  const [draftSaveMessage, setDraftSaveMessage] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    const savedDraft = localStorage.getItem('leadDraft');
    if (savedDraft) {
      try {
        const parsedDraft = JSON.parse(savedDraft);
        // Migrate old enum values to new format
        const migratedDraft = migrateEnumValues(parsedDraft);
        // Always ensure propertyState is set to Hawaii
        migratedDraft.propertyState = 'Hawaii';
        setFormData(migratedDraft);
        // Update localStorage with migrated values
        localStorage.setItem('leadDraft', JSON.stringify(migratedDraft));
      } catch (error) {
        console.error('Error loading draft:', error);
        // Clear corrupted localStorage data
        localStorage.removeItem('leadDraft');
      }
    }
  }, []);

  useEffect(() => {
    const autoSaveTimer = setTimeout(() => {
      if (Object.values(formData)?.some(value => value !== '')) {
        localStorage.setItem('leadDraft', JSON.stringify(formData));
      }
    }, 2000);

    return () => clearTimeout(autoSaveTimer);
  }, [formData]);

  useEffect(() => {
    // Calculate completion based on REQUIRED fields only
    // Required fields according to database schema:
    // 1. customerFirstName, 2. customerLastName, 3. customerEmail, 4. customerPhone
    // 5. propertyType, 6. homeOwner, 7. referralType
    const requiredFields = [
      formData?.customerFirstName,
      formData?.customerLastName,
      formData?.customerEmail,
      formData?.customerPhone,
      formData?.propertyType,
      formData?.homeOwner,
      formData?.referralType
    ];

    const filledRequiredFields = requiredFields?.filter(value => value !== '')?.length;
    const totalRequiredFields = 7;
    
    setCompletionPercentage(Math.round((filledRequiredFields / totalRequiredFields) * 100));

    // Update section completion status
    const customerComplete = formData?.customerFirstName && formData?.customerLastName && 
                            formData?.customerEmail && formData?.customerPhone;
    
    const propertyComplete = formData?.propertyType && formData?.homeOwner;
    
    const referralComplete = formData?.referralType;

    setCompletedSections({
      customer: !!customerComplete,
      property: !!propertyComplete,
      referral: !!referralComplete
    });
  }, [formData]);

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateFormForDraft = () => {
    // For draft, we need at least customer name and email OR property info
    const hasCustomerInfo = formData?.customerFirstName && formData?.customerEmail;
    const hasPropertyInfo = formData?.propertyType;
    
    if (!hasCustomerInfo && !hasPropertyInfo) {
      setErrors({ 
        general: 'Please provide at least customer name and email, or property type to save as draft' 
      });
      return false;
    }
    
    return true;
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.customerFirstName?.trim()) newErrors.customerFirstName = 'First name is required';
    if (!formData?.customerLastName?.trim()) newErrors.customerLastName = 'Last name is required';
    if (!formData?.customerEmail?.trim()) {
      newErrors.customerEmail = 'Email is required';
    } else if (!/\S+@\S+\.\S+/?.test(formData?.customerEmail)) {
      newErrors.customerEmail = 'Email is invalid';
    }
    if (!formData?.customerPhone?.trim()) newErrors.customerPhone = 'Phone number is required';

    if (!formData?.propertyType) newErrors.propertyType = 'Property type is required';
    if (!formData?.homeOwner) newErrors.homeOwner = 'Home owner status is required';
    if (!formData?.propertyAddress?.trim()) newErrors.propertyAddress = 'Property address is required';
    if (!formData?.propertyCity?.trim()) newErrors.propertyCity = 'City is required';
    if (!formData?.propertyState?.trim()) newErrors.propertyState = 'State is required';
    if (!formData?.propertyZip?.trim()) newErrors.propertyZip = 'ZIP code is required';

    if (!formData?.referralType) newErrors.referralType = 'Referral type is required';

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    // Validate form before submission
    if (!validateForm()) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    
    setIsSubmitting(true);
    setError('');

    try {
      // Transform form data to match service expectations
      const leadData = {
        customerName: `${formData?.customerFirstName?.trim()} ${formData?.customerLastName?.trim()}`,
        email: formData?.customerEmail,
        phone: formData?.customerPhone,
        propertyType: formData?.propertyType,
        homeOwner: formData?.homeOwner,
        propertyAddress: formData?.propertyAddress,
        propertyCity: formData?.propertyCity,
        propertyState: formData?.propertyState,
        propertyZip: formData?.propertyZip,
        referralType: formData?.referralType,
        referralNotes: formData?.notes || null
      };
      
      const result = await leadService?.createLead(leadData);
      
      // Extract Salesforce ID from result and set it for the success modal
      if (result?.salesforceSync?.success && result?.salesforceSync?.salesforceId) {
        setSalesforceLeadId(result?.salesforceSync?.salesforceId);
      } else if (result?.salesforce_id) {
        // Fallback: check if salesforce_id exists directly in result
        setSalesforceLeadId(result?.salesforce_id);
      }
      
      // Show success message with Salesforce sync status
      let successMessage = 'Lead created successfully!';
      
      if (result?.salesforceSync) {
        if (result?.salesforceSync?.success) {
          successMessage += ' Synced to Salesforce.';
        } else if (result?.salesforceSync?.skipped) {
          successMessage += ' (Salesforce sync requires backend setup)';
        } else {
          successMessage += ' (Salesforce sync failed - lead saved locally)';
        }
      }
      
      setSuccessMessage(successMessage);
      setShowSuccessModal(true);
      
      // Clear localStorage draft after successful submission
      localStorage.removeItem('leadDraft');
      setCurrentDraftId(null);
      
      // Reset form
      setFormData({
        customerFirstName: '',
        customerLastName: '',
        customerEmail: '',
        customerPhone: '',
        customerAddress: '',
        customerCity: '',
        customerState: '',
        customerZip: '',
        propertyType: '',
        homeOwner: '',
        estimatedValue: '',
        propertyAddress: '',
        propertyCity: '',
        propertyState: 'Hawaii',
        propertyZip: '',
        referralType: '',
        notes: ''
      });
    } catch (err) {
      console.error('Error creating lead:', err);
      setError(err?.message || 'Failed to create lead. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!validateFormForDraft()) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setIsSavingDraft(true);
    setDraftSaveMessage('');
    
    try {
      // Prepare draft data
      const draftData = {
        customerName: formData?.customerFirstName && formData?.customerLastName 
          ? `${formData?.customerFirstName} ${formData?.customerLastName}`
          : formData?.customerFirstName || 'Draft Lead',
        email: formData?.customerEmail || '',
        phone: formData?.customerPhone || null,
        propertyType: formData?.propertyType || null,
        homeOwner: formData?.homeOwner || null,
        propertyAddress: formData?.propertyAddress || null,
        propertyCity: formData?.propertyCity || null,
        propertyState: formData?.propertyState || null,
        propertyZip: formData?.propertyZip || null,
        referralType: formData?.referralType || null,
        referralNotes: formData?.notes || null
      };

      // Save draft to database
      const savedDraft = await leadService?.saveDraft(draftData, currentDraftId);
      
      // Update current draft ID if this was a new draft
      if (!currentDraftId) {
        setCurrentDraftId(savedDraft?.id);
      }
      
      // Also save to localStorage as backup
      localStorage.setItem('leadDraft', JSON.stringify(formData));
      
      setDraftSaveMessage('Draft saved successfully!');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setDraftSaveMessage('');
      }, 3000);
    } catch (error) {
      console.error('Error saving draft:', error);
      setDraftSaveMessage(`Failed to save draft: ${error?.message || 'Unknown error'}`);
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleCloseModal = () => {
    setShowSuccessModal(false);
    navigate('/partner-dashboard');
  };

  const handleCreateAnother = () => {
    setShowSuccessModal(false);
    setFormData({
      customerFirstName: '',
      customerLastName: '',
      customerEmail: '',
      customerPhone: '',
      customerAddress: '',
      customerCity: '',
      customerState: '',
      customerZip: '',
      propertyType: '',
      homeOwner: '',
      estimatedValue: '',
      propertyAddress: '',
      propertyCity: '',
      propertyState: 'Hawaii',
      propertyZip: '',
      referralType: '',
      notes: ''
    });
    setErrors({});
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-background">
      <AuthenticatedHeader />
      <NavigationBreadcrumbs />
      <main className="main-content pt-20 px-6 pb-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-semibold text-foreground mb-2">Create New Referral</h1>
              <p className="text-muted-foreground">
                
              </p>
            </div>
            <Button
              variant="ghost"
              iconName="ArrowLeft"
              iconPosition="left"
              onClick={() => navigate('/partner-dashboard')}
            >
              Back to Dashboard
            </Button>
          </div>

          {errors?.general && (
            <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive">
              {errors?.general}
            </div>
          )}

          {draftSaveMessage && (
            <div className={`mb-6 p-4 rounded-lg ${
              draftSaveMessage?.includes('Failed') 
                ? 'bg-destructive/10 border border-destructive/20 text-destructive' :'bg-green-50 border border-green-200 text-green-800'
            }`}>
              {draftSaveMessage}
            </div>
          )}

          <FormProgressIndicator 
            completionPercentage={completionPercentage}
            completedSections={completedSections}
          />

          <form onSubmit={handleSubmit} className="space-y-6">
            <CustomerInfoSection 
              formData={formData}
              errors={errors}
              onChange={handleChange}
            />

            <PropertyInfoSection 
              formData={formData}
              errors={errors}
              onChange={handleChange}
              onSelectChange={handleSelectChange}
            />

            <ReferralInfoSection 
              formData={formData}
              errors={errors}
              onChange={handleChange}
            />

            <div className="flex flex-col sm:flex-row gap-4 justify-end pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleSaveDraft}
                loading={isSavingDraft}
                iconName="Save"
                iconPosition="left"
                disabled={isSubmitting}
              >
                {isSavingDraft ? 'Saving Draft...' : 'Save as Draft'}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate('/partner-dashboard')}
                disabled={isSubmitting || isSavingDraft}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="default"
                loading={isSubmitting}
                iconName="Send"
                iconPosition="left"
                disabled={isSavingDraft}
              >
                {isSubmitting ? 'Submitting to Salesforce...' : 'Submit Lead'}
              </Button>
            </div>
          </form>
        </div>
      </main>
      <SuccessModal 
        isOpen={showSuccessModal}
        salesforceLeadId={salesforceLeadId}
        onClose={handleCloseModal}
        onCreateAnother={handleCreateAnother}
      />
    </div>
  );
};

export default CreateLead;